import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Check, Eye, FilePlus2, FileSignature, PenLine, Trash2, Upload } from 'lucide-react';
import {
  BoutonMint,
  BoutonPrincipal,
  BoutonSecondaire,
  Carte,
  Chargement,
  EnteteEcran,
  MessageErreur,
  Pastille,
} from '../components/ui/Primitives';
import { useToast } from '../components/ui/useToast';
import { VisionneusePiece } from '../components/dossier/VisionneusePiece';
import { CLASSE_ACTION_LIGNE, PiecesFormateur } from '../components/formateurs/PiecesFormateur';
import { FacturesFormateur } from '../components/formateurs/FacturesFormateur';
import {
  CLASSE_CHAMP_FORMATEUR,
  ChampFormateur,
  ModaleConvention,
  ModaleDepotPiece,
  ModaleFacture,
} from '../components/formateurs/ModalesFormateur';
import { useAuth } from '../auth/useAuth';
import { detailsRefus } from '../lib/erreurs-api';
import {
  dateReunion,
  heures,
  jourCourt,
  messageRefus,
  urlConventionFormateur,
  urlFactureFormateur,
  urlPieceFormateur,
  useCorrigerFacture,
  useDeposerPieceFormateur,
  useEnregistrerCoordonnees,
  useGenererMaConvention,
  useMaFicheFormateur,
  useRetirerPieceFormateur,
  useTransmettreFacture,
  type EtapeCircuit,
  type FactureFormateur,
  type FicheFormateur,
  type PieceFormateur,
  type SaisieCoordonnees,
} from '../lib/useFormateurs';

const TITRE = 'Mes documents & ma facturation';
const SOUS_TITRE = 'Espace formateur · pièces obligatoires, convention et factures';

interface Consultation {
  libelle: string;
  url: string;
  nomFichier: string;
  typeMime: string;
}

/**
 * Écran « Mes documents & ma facturation » du formateur (CDC §7.4).
 *
 * Le formateur y dépose ses pièces obligatoires, renseigne SIRET et
 * coordonnées bancaires, génère sa convention et transmet ses factures,
 * vérifiées ensuite par l'assistant(e) pédagogique ou administratif. Tout
 * vient de l'API : l'état des pièces, ce qui empêche un geste, le circuit.
 */
export function FormFacturationPage(): JSX.Element {
  const { utilisateur, peutAcceder } = useAuth();
  const { data, isLoading, error } = useMaFicheFormateur();

  if (isLoading) return <Chargement />;
  if (error) return <MessageErreur message={messageRefus(error, 'Espace indisponible.')} />;

  if (!data?.fiche) {
    const estFormateur = utilisateur?.profil === 'FORMATEUR';
    return (
      <div className="mx-auto max-w-[1500px] space-y-6 p-4 sm:p-6">
        <EnteteEcran titre={TITRE} sousTitre={SOUS_TITRE} />
        <div className="rounded-2xl bg-white p-4 text-sm text-codialis-navy shadow-card sm:p-6">
          {estFormateur ? (
            'Votre fiche formateur n’est pas encore créée : le pôle pédagogique la crée depuis l’écran « Formateurs ». Vos dépôts et vos factures seront possibles ensuite.'
          ) : (
            <>
              Cet espace est celui du formateur.{' '}
              {peutAcceder('instructors') ? (
                <>
                  Les pièces, conventions et factures des formateurs se consultent dans{' '}
                  <Link to="/instructors" className="font-semibold text-codialis-sky hover:underline">
                    l’écran « Formateurs »
                  </Link>
                  .
                </>
              ) : (
                'Les pièces et factures des formateurs sont vérifiées par le pôle pédagogique.'
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  return <EspaceFormateur fiche={data.fiche} />;
}

function EspaceFormateur({ fiche }: { fiche: FicheFormateur }): JSX.Element {
  const toast = useToast();
  const deposer = useDeposerPieceFormateur();
  const retirer = useRetirerPieceFormateur();
  const genererConvention = useGenererMaConvention();
  const transmettre = useTransmettreFacture();
  const corriger = useCorrigerFacture();

  const [pieceADeposer, setPieceADeposer] = useState<PieceFormateur | null>(null);
  const [factureOuverte, setFactureOuverte] = useState<FactureFormateur | 'NOUVELLE' | null>(null);
  const [conventionOuverte, setConventionOuverte] = useState(false);
  const [consultation, setConsultation] = useState<Consultation | null>(null);

  const independant = fiche.statut === 'INDEPENDANT';
  const derniereFacture = fiche.factures[0] ?? null;
  const heuresFacturables = fiche.fichesFacturables.reduce((total, ligne) => total + ligne.heures, 0);

  const retirerDepot = (documentId: string): void => {
    retirer.mutate(documentId, {
      onSuccess: () => toast('Pièce retirée'),
      onError: (erreur) => toast(messageRefus(erreur, 'Retrait impossible.')),
    });
  };

  return (
    <div className="mx-auto max-w-[1500px] space-y-6 p-4 sm:p-6">
      <EnteteEcran
        titre={TITRE}
        sousTitre={SOUS_TITRE}
        actions={
          independant ? (
            <>
              <BoutonSecondaire onClick={() => setConventionOuverte(true)} disabled={fiche.refusConvention !== null}>
                Convention de formation
              </BoutonSecondaire>
              <BoutonPrincipal onClick={() => setFactureOuverte('NOUVELLE')} disabled={fiche.refusTransmission !== null}>
                Déposer ma facture
              </BoutonPrincipal>
            </>
          ) : undefined
        }
      />

      {independant && fiche.refusTransmission && (
        <p className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          {fiche.refusTransmission}
        </p>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="min-w-0 space-y-6 lg:col-span-2">
          <Carte titre="Documents obligatoires du formateur" sansBordure>
            <p className="px-4 pt-3 text-xs text-codialis-gray sm:px-6">
              {independant
                ? 'Pièces justificatives réglementaires et contractuelles, vérifiées par l’assistant(e) pédagogique ou administratif.'
                : 'Formateur salarié : seule votre qualification est à justifier, votre rémunération passe par la paie.'}
            </p>
            <PiecesFormateur
              pieces={fiche.pieces}
              actionsPiece={(piece) => {
                if (!piece.exigee) return null;
                if (piece.type === 'CONVENTION_SIGNEE' && fiche.conventions.length === 0) {
                  return <span className="text-[11px] text-codialis-gray">Générez d’abord la convention</span>;
                }
                return (
                  <button
                    type="button"
                    onClick={() => setPieceADeposer(piece)}
                    className={`${CLASSE_ACTION_LIGNE} border border-gray-200 bg-white text-codialis-navy hover:bg-gray-50`}
                  >
                    <Upload className="h-3.5 w-3.5" aria-hidden="true" />
                    {piece.depots.length === 0 ? 'Déposer' : piece.multiple ? 'Ajouter' : 'Remplacer'}
                  </button>
                );
              }}
              actionsDepot={(depot, piece) => (
                <>
                  <button
                    type="button"
                    onClick={() =>
                      setConsultation({
                        libelle: piece.libelle,
                        url: urlPieceFormateur(depot.id),
                        nomFichier: depot.nomFichier,
                        typeMime: depot.typeMime,
                      })
                    }
                    className={`${CLASSE_ACTION_LIGNE} text-codialis-sky hover:bg-white`}
                  >
                    <Eye className="h-3.5 w-3.5" aria-hidden="true" />
                    Voir
                  </button>
                  {depot.refusRetrait === null && (
                    <button
                      type="button"
                      onClick={() => retirerDepot(depot.id)}
                      disabled={retirer.isPending}
                      className={`${CLASSE_ACTION_LIGNE} text-red-700 hover:bg-white`}
                    >
                      <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                      Retirer
                    </button>
                  )}
                </>
              )}
            />
          </Carte>

          {independant && <CoordonneesFacturation fiche={fiche} />}

          {independant && (
            <Carte
              titre="Mes factures"
              actions={
                <BoutonPrincipal onClick={() => setFactureOuverte('NOUVELLE')} disabled={fiche.refusTransmission !== null}>
                  <FilePlus2 className="mr-1.5 h-4 w-4" aria-hidden="true" />
                  Déposer ma facture
                </BoutonPrincipal>
              }
              sansBordure
            >
              <div className="border-b border-gray-100 px-4 py-3 text-xs text-codialis-gray sm:px-6">
                {fiche.fichesFacturables.length > 0 ? (
                  <>
                    <span className="font-semibold text-codialis-navy">
                      {fiche.fichesFacturables.length} fiche{fiche.fichesFacturables.length > 1 ? 's' : ''} séquence
                      validée{fiche.fichesFacturables.length > 1 ? 's' : ''} non facturée
                      {fiche.fichesFacturables.length > 1 ? 's' : ''} · {heures(heuresFacturables)}
                    </span>
                    {fiche.tauxHoraire !== null && ` · taux convenu ${fiche.tauxHoraire.toLocaleString('fr-FR')} € HT de l’heure`}
                    .
                  </>
                ) : (
                  'Aucune fiche séquence validée en attente de facturation.'
                )}{' '}
                Seules les séquences validées par le responsable pédagogique sont rattachées à une facture.
              </div>
              <FacturesFormateur
                factures={fiche.factures}
                siVide="Aucune facture transmise pour l’instant."
                actions={(facture) => (
                  <>
                    <button
                      type="button"
                      onClick={() =>
                        setConsultation({
                          libelle: `Facture n° ${facture.numero}`,
                          url: urlFactureFormateur(facture.id),
                          nomFichier: facture.nomFichier,
                          typeMime: 'application/pdf',
                        })
                      }
                      className={`${CLASSE_ACTION_LIGNE} border border-gray-200 bg-white text-codialis-navy hover:bg-gray-50`}
                    >
                      <Eye className="h-3.5 w-3.5" aria-hidden="true" />
                      Voir la facture
                    </button>
                    {facture.refusCorrection === null && (
                      <button
                        type="button"
                        onClick={() => setFactureOuverte(facture)}
                        className={`${CLASSE_ACTION_LIGNE} border border-gray-200 bg-white text-codialis-navy hover:bg-gray-50`}
                      >
                        <PenLine className="h-3.5 w-3.5" aria-hidden="true" />
                        {facture.statut === 'A_CORRIGER' ? 'Corriger' : 'Modifier'}
                      </button>
                    )}
                  </>
                )}
              />
            </Carte>
          )}
        </div>

        <div className="min-w-0 space-y-6">
          {independant ? (
            <>
              <CircuitValidation facture={derniereFacture} />

              <Carte titre="Fréquence de traitement" sansBordure>
                <div className="space-y-2 p-4 sm:p-6">
                  <div className="flex items-center justify-between rounded-xl bg-gray-50 px-3 py-2.5">
                    <span className="text-sm text-codialis-navy">Formations CFA</span>
                    <Pastille texte="1×/semaine" ton="info" />
                  </div>
                  <div className="flex items-center justify-between rounded-xl bg-gray-50 px-3 py-2.5">
                    <span className="text-sm text-codialis-navy">Formations OF</span>
                    <Pastille texte="1×/jour" ton="mint" />
                  </div>
                  <p className="pt-1 text-[11px] text-codialis-gray">
                    Le traitement des dossiers de facturation suit le type de formation concerné.
                  </p>
                </div>
              </Carte>

              <Carte titre="Convention de formation" sansBordure>
                <div className="space-y-3 p-4 sm:p-6">
                  <p className="text-sm text-codialis-gray">
                    Générée à partir de vos données et des séances qui vous sont attribuées. Téléchargez-la,
                    signez-la à la main, puis déposez l’exemplaire signé dans vos documents obligatoires.
                  </p>
                  {fiche.conventions.length > 0 && (
                    <ul className="space-y-2">
                      {fiche.conventions.map((convention, rang) => (
                        <li key={convention.id} className="rounded-xl bg-gray-50 px-3 py-2.5">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <span className="text-sm font-medium text-codialis-navy">
                              Du {jourCourt(convention.debut)} au {jourCourt(convention.fin)}
                            </span>
                            {convention.exemplaire ? (
                              <Pastille
                                texte={`Signée · ${convention.exemplaire.statutLibelle.toLowerCase()}`}
                                ton={convention.exemplaire.statutTon}
                              />
                            ) : (
                              <Pastille texte={rang === 0 ? 'À signer' : 'Non signée'} ton={rang === 0 ? 'warning' : 'neutral'} />
                            )}
                          </div>
                          <div className="mt-0.5 text-[11px] text-codialis-gray">
                            Générée le {dateReunion(convention.genereLe)}
                            {convention.heuresPrevues !== null && ` · ${heures(convention.heuresPrevues)} prévues`}
                          </div>
                          <button
                            type="button"
                            onClick={() =>
                              setConsultation({
                                libelle: 'Convention de formation',
                                url: urlConventionFormateur(convention.id),
                                nomFichier: convention.nomFichier,
                                typeMime: 'application/pdf',
                              })
                            }
                            className={`${CLASSE_ACTION_LIGNE} mt-1 -ml-3 text-codialis-sky hover:bg-white`}
                          >
                            <Eye className="h-3.5 w-3.5" aria-hidden="true" />
                            Voir et télécharger
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                  <BoutonMint onClick={() => setConventionOuverte(true)}>
                    <FileSignature className="mr-1.5 h-4 w-4" aria-hidden="true" />
                    {fiche.conventions.length > 0 ? 'Générer une nouvelle convention' : 'Générer ma convention'}
                  </BoutonMint>
                </div>
              </Carte>
            </>
          ) : (
            <Carte titre="Formateur salarié" sansBordure>
              <p className="p-4 text-sm text-codialis-gray sm:p-6">
                Votre rémunération passe par la paie : vous n’avez ni facture à transmettre ni convention à
                signer. Si votre statut est inexact, signalez-le au pôle pédagogique.
              </p>
            </Carte>
          )}
        </div>
      </div>

      {pieceADeposer && (
        <ModaleDepotPiece
          piece={pieceADeposer}
          enCours={deposer.isPending}
          onFermer={() => setPieceADeposer(null)}
          onDeposer={async (entree) => {
            await deposer.mutateAsync({ type: pieceADeposer.type, ...entree });
            setPieceADeposer(null);
            toast('Pièce déposée · en attente de vérification');
          }}
        />
      )}

      {factureOuverte && (
        <ModaleFacture
          fiche={fiche}
          facture={factureOuverte === 'NOUVELLE' ? null : factureOuverte}
          enCours={transmettre.isPending || corriger.isPending}
          onFermer={() => setFactureOuverte(null)}
          onEnvoyer={async (saisie) => {
            if (factureOuverte === 'NOUVELLE') {
              await transmettre.mutateAsync(saisie);
            } else {
              await corriger.mutateAsync({ factureId: factureOuverte.id, saisie });
            }
            setFactureOuverte(null);
            toast('Facture transmise · en attente de vérification');
          }}
        />
      )}

      {conventionOuverte && (
        <ModaleConvention
          enCours={genererConvention.isPending}
          onFermer={() => setConventionOuverte(false)}
          onGenerer={async ({ debut, fin }) => {
            await genererConvention.mutateAsync({ debut, fin });
            setConventionOuverte(false);
            toast('Convention générée · à signer puis déposer');
          }}
        />
      )}

      {consultation && (
        <VisionneusePiece
          ouverte
          libellePiece={consultation.libelle}
          fichiers={[
            {
              id: 'fichier',
              nomFichier: consultation.nomFichier,
              typeMime: consultation.typeMime,
              tailleOctets: 0,
              deposeLe: '',
            },
          ]}
          urlFichier={() => consultation.url}
          onFermer={() => setConsultation(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Coordonnées de facturation
// ---------------------------------------------------------------------------

function CoordonneesFacturation({ fiche }: { fiche: FicheFormateur }): JSX.Element {
  const toast = useToast();
  const enregistrer = useEnregistrerCoordonnees();
  const { coordonnees } = fiche;
  const [saisie, setSaisie] = useState<SaisieCoordonnees>({
    siret: coordonnees.siret ?? '',
    raisonSociale: coordonnees.raisonSociale ?? '',
    adresse: coordonnees.adresse ?? '',
    codePostal: coordonnees.codePostal ?? '',
    ville: coordonnees.ville ?? '',
    iban: coordonnees.iban ?? '',
    bic: coordonnees.bic ?? '',
    titulaireCompte: coordonnees.titulaireCompte ?? '',
  });
  const [refus, setRefus] = useState<Record<string, string>>({});
  const [refusGlobal, setRefusGlobal] = useState<string | null>(null);

  const changer = (champ: keyof SaisieCoordonnees) => (valeur: string) =>
    setSaisie((actuelle) => ({ ...actuelle, [champ]: valeur }));

  const soumettre = (evenement: FormEvent): void => {
    evenement.preventDefault();
    setRefus({});
    setRefusGlobal(null);
    enregistrer.mutate(saisie, {
      onSuccess: () => toast('Coordonnées enregistrées'),
      onError: (erreur) => {
        const details = detailsRefus(erreur);
        if (details.length > 0) {
          setRefus(Object.fromEntries(details.map((detail) => [detail.champ, detail.message])));
        } else {
          setRefusGlobal(messageRefus(erreur, 'Enregistrement impossible.'));
        }
      },
    });
  };

  const champ = (
    nom: keyof SaisieCoordonnees,
    libelle: string,
    options: { mono?: boolean; aide?: string; inputMode?: 'numeric' | 'text'; autoComplete?: string } = {},
  ): JSX.Element => (
    <ChampFormateur libelle={libelle} aide={refus[nom] ?? options.aide}>
      <input
        value={saisie[nom]}
        onChange={(e) => changer(nom)(e.target.value)}
        inputMode={options.inputMode}
        autoComplete={options.autoComplete ?? 'off'}
        aria-invalid={refus[nom] ? true : undefined}
        className={`${CLASSE_CHAMP_FORMATEUR} ${options.mono ? 'font-mono' : ''} ${
          refus[nom] ? 'border-red-300 bg-red-50' : ''
        }`}
      />
    </ChampFormateur>
  );

  return (
    <Carte titre="Coordonnées de facturation" sansBordure>
      <form onSubmit={soumettre} className="space-y-4 p-4 sm:p-6">
        <p className="text-xs text-codialis-gray">
          Recopiées sur chaque facture au moment de sa transmission. L’IBAN est conservé chiffré.
          {coordonnees.modifieesLe && ` Dernière mise à jour le ${dateReunion(coordonnees.modifieesLe)}.`}
        </p>
        {/* L'IBAN compte jusqu'à 34 caractères en chasse fixe : il garde la
            plus large colonne, pour se lire entier sans défiler dans le champ. */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_2fr]">
          {champ('siret', 'SIRET', { mono: true, inputMode: 'numeric', aide: '14 chiffres' })}
          {champ('iban', 'IBAN', { mono: true })}
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_2fr]">
          {champ('bic', 'BIC', { mono: true })}
          {champ('titulaireCompte', 'Titulaire du compte')}
        </div>
        {champ('raisonSociale', 'Raison sociale', { aide: 'Si vous facturez sous un autre nom que le vôtre' })}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-[2fr_1fr_1.5fr]">
          {champ('adresse', 'Adresse', { autoComplete: 'street-address' })}
          {champ('codePostal', 'Code postal', { inputMode: 'numeric', autoComplete: 'postal-code' })}
          {champ('ville', 'Ville', { autoComplete: 'address-level2' })}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <span className="text-xs text-codialis-gray">
            Statut : <span className="font-semibold text-codialis-navy">{fiche.statutLibelle}</span> · fixé par le pôle
            pédagogique
          </span>
          <BoutonPrincipal type="submit" disabled={enregistrer.isPending}>
            {enregistrer.isPending ? 'Enregistrement…' : 'Enregistrer'}
          </BoutonPrincipal>
        </div>
        {refusGlobal && <p className="rounded-xl bg-red-50 px-3 py-2 text-sm text-red-800">{refusGlobal}</p>}
      </form>
    </Carte>
  );
}

// ---------------------------------------------------------------------------
// Circuit de validation
// ---------------------------------------------------------------------------

/** Étapes à venir d'une première facture, avant tout dépôt. */
const CIRCUIT_VIDE: EtapeCircuit[] = [
  { etape: 'FORMATEUR', libelle: 'Formateur', detail: 'SIRET, RIB, montant', etat: 'EN_COURS', precision: null },
  {
    etape: 'VERIFICATION',
    libelle: 'Assistant(e) pédagogique / administratif',
    detail: 'Vérification des éléments',
    etat: 'A_VENIR',
    precision: null,
  },
  { etape: 'FACTURATION', libelle: 'Pôle facturation', detail: 'Contrôle et validation', etat: 'A_VENIR', precision: null },
  { etape: 'DIRECTION', libelle: 'Direction', detail: 'Validation et paiement', etat: 'A_VENIR', precision: null },
];

/** Où en est la dernière facture : les étapes viennent de l'API (TF-401). */
function CircuitValidation({ facture }: { facture: FactureFormateur | null }): JSX.Element {
  const etapes = facture?.circuit.length ? facture.circuit : CIRCUIT_VIDE;

  return (
    <Carte titre="Circuit de validation" sansBordure>
      <div className="space-y-4 p-4 sm:p-6">
        <p className="text-xs text-codialis-gray">
          {facture ? `Dernière facture : n° ${facture.numero}` : 'Aucune facture transmise pour l’instant.'}
        </p>
        <ol className="space-y-4">
          {etapes.map((ligne, rang) => (
            <li key={ligne.etape} className="flex items-start gap-3">
              <span
                className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                  ligne.etat === 'FAIT'
                    ? 'bg-gradient-to-br from-codialis-mint to-codialis-teal text-codialis-navy'
                    : ligne.etat === 'EN_COURS'
                      ? 'bg-codialis-navy text-white'
                      : ligne.etat === 'A_CORRIGER'
                        ? 'bg-red-100 text-red-700'
                        : 'bg-gray-100 text-codialis-gray'
                }`}
              >
                {ligne.etat === 'FAIT' ? <Check className="h-4 w-4" aria-label="Fait" /> : rang + 1}
              </span>
              <div className="min-w-0">
                <div className="text-sm font-medium text-codialis-navy">{ligne.libelle}</div>
                <div className="text-[11px] text-codialis-gray">
                  {ligne.detail}
                  {ligne.precision ? ` · ${ligne.precision.charAt(0).toLocaleLowerCase('fr')}${ligne.precision.slice(1)}` : ''}
                </div>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </Carte>
  );
}
